import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard, Package, Users, Truck, BarChart2,
  LogOut, Menu, Settings, ClipboardList, Tag, ScanLine, Receipt, Wallet, ShieldAlert,
  ChevronDown, ChevronRight, Banknote, Landmark, CalendarDays, FileText, DollarSign, FileDown, MapPin,
  TrendingUp, Hourglass, Mail, Fuel, Printer, Smartphone, HandCoins,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { GlobalLoadingBar } from './Loading';
import api from '../api';
import { can, refreshUser } from '../auth';

// A driver SOS must never be a quiet red banner you scroll past. For admins,
// this watches for open alerts on every page and, when a NEW one arrives, sounds
// an alarm and fires a browser notification — the immediate at-the-desk warning
// that Telegram/email can't give while you're looking at the screen.
function SosWatcher({ isAdmin }) {
  const alarmedRef = React.useRef(new Set());

  const { data: sos = [] } = useQuery({
    queryKey: ['sos-open'],
    queryFn: () => api.get('/drivers/sos').then((r) => r.data),
    enabled: isAdmin,
    refetchInterval: 20000,
    retry: false,
  });

  // Ask once so the browser notification can fire later
  React.useEffect(() => {
    if (isAdmin && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, [isAdmin]);

  const beep = React.useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      [0, 0.5, 1].forEach((t) => {   // three urgent beeps
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.type = 'square'; o.frequency.value = 880;
        o.connect(g); g.connect(ctx.destination);
        g.gain.setValueAtTime(0.0001, ctx.currentTime + t);
        g.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.35);
        o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.36);
      });
      setTimeout(() => ctx.close().catch(() => {}), 2000);
    } catch {}
  }, []);

  React.useEffect(() => {
    for (const a of sos) {
      if (alarmedRef.current.has(a.id)) continue;
      alarmedRef.current.add(a.id);
      // Skip alarms already seen before this tab loaded (avoid a blast on refresh)
      if (Date.now() - new Date(a.created_at).getTime() > 10 * 60 * 1000) continue;
      beep();
      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          new Notification(`🆘 SOS from ${a.driver_name}`, {
            body: 'Driver has raised an emergency. Open the Driver Map.',
            requireInteraction: true,
          });
        } catch {}
      }
    }
  }, [sos, beep]);

  if (!isAdmin || sos.length === 0) return null;
  return (
    <Link to="/drivers/map"
      className="bg-red-600 text-white px-4 py-2.5 flex items-center gap-3 border-b border-red-700 hover:bg-red-700">
      <ShieldAlert size={18} className="flex-shrink-0 animate-pulse" />
      <p className="text-sm flex-1 font-semibold">
        🆘 {sos.length} open SOS alert{sos.length === 1 ? '' : 's'} — {sos.map((a) => a.driver_name).join(', ')}
      </p>
      <span className="text-xs font-semibold bg-white text-red-700 px-3 py-1 rounded-lg flex-shrink-0">Open Driver Map</span>
    </Link>
  );
}

// Shown on every page, to everyone, while backups are broken. Backups fail
// silently by nature — nobody notices until they need one — so this deliberately
// sits in the way rather than hiding in Settings. Everyone sees it so whoever is
// at the desk can raise it even when the admin isn't logged in; only admins get
// the button, since only they can act (and see the Database tab).
function BackupWarning() {
  const { data } = useQuery({
    queryKey: ['backup-health'],
    queryFn: () => api.get('/settings/backup/health').then((r) => r.data),
    refetchInterval: 30 * 60 * 1000,  // re-check every 30 min
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  if (!data || data.healthy) return null;

  const isError = data.severity === 'error';
  return (
    <div className={`px-4 py-2.5 flex items-center gap-3 border-b ${
      isError ? 'bg-red-600 text-white border-red-700' : 'bg-amber-50 text-amber-900 border-amber-200'
    }`}>
      <ShieldAlert size={18} className="flex-shrink-0" />
      <p className="text-sm flex-1">
        <strong>{isError ? 'Backups are not working.' : 'Backups need attention.'}</strong>{' '}
        {data.problem}
      </p>
      {data.can_fix && (
        <Link to="/settings?tab=database"
          className={`text-xs font-semibold px-3 py-1.5 rounded-lg flex-shrink-0 ${
            isError ? 'bg-white text-red-700 hover:bg-red-50' : 'bg-amber-600 text-white hover:bg-amber-700'
          }`}>
          Check backups
        </Link>
      )}
    </div>
  );
}

// The three you reach for constantly stay at the top level; the rest live in
// groups you can fold away. A group opens itself when you're on one of its
// pages, so you're never looking at a collapsed menu wondering where you are.
const nav = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/parcels',   icon: Package,         label: 'Parcels', perm: 'parcels.view' },
  { to: '/clients',   icon: Users,           label: 'Clients', perm: 'clients.view' },
  {
    // Depot staff take money at the counter — payments, cash-up, petty cash and
    // chasing debt — but never see the whole financial picture.
    group: 'money', label: 'Money', icon: Wallet, items: [
      // Ordered by how often it's used at the counter — taking money first.
      { to: '/payments',        icon: Wallet,       label: 'Receive Payment', perm: 'billing.payments' },
      { to: '/invoices',        icon: Receipt,      label: 'Invoices', perm: 'billing.view' },
      { to: '/billing',         icon: Package,      label: 'Billing', perm: 'billing.view' },
      { to: '/cash-up',         icon: Banknote,     label: 'Cash-up', perm: 'billing.payments' },
      { to: '/fuel',            icon: Fuel,         label: 'Fuel', perm: 'reports.financial' },
      { to: '/driver-pay',      icon: HandCoins,    label: 'Driver Pay', perm: 'reports.financial' },
      { to: '/bank-import',     icon: Landmark,     label: 'Bank Import', perm: 'billing.bank' },
      { to: '/month-end',       icon: CalendarDays, label: 'Month-end', perm: 'billing.statements' },
      { to: '/debt-collection', icon: Wallet,       label: 'Debt Collection', perm: 'billing.payments' },
    ],
  },
  {
    // Every report here is turnover, VAT or profit — admin only.
    group: 'reports', label: 'Reports', icon: BarChart2, items: [
      { to: '/reports',                 icon: BarChart2,  label: 'Operations', perm: 'reports.operational' },
      { to: '/reports/revenue',         icon: DollarSign, label: 'Revenue', perm: 'reports.financial' },
      { to: '/reports/sales',           icon: Receipt,    label: 'Sales', perm: 'reports.financial' },
      { to: '/reports/payments',        icon: Wallet,     label: 'Payments Received', perm: 'reports.financial' },
      { to: '/reports/vat',             icon: FileText,   label: 'VAT (VAT201)', perm: 'reports.financial' },
      { to: '/reports/income-expenses', icon: TrendingUp, label: 'Income vs Expenses', perm: 'reports.financial' },
      { to: '/reports/uninvoiced',      icon: Hourglass,  label: 'Uninvoiced', perm: 'billing.view' },
      { to: '/pastel-export',           icon: FileDown,   label: 'Pastel Export', perm: 'reports.financial' },
    ],
  },
  {
    group: 'depot', label: 'Depot & Labels', icon: ScanLine, items: [
      { to: '/drivers',        icon: Truck,    label: 'Drivers', perm: 'parcels.assign' },
      { to: '/runsheets',      icon: Printer,  label: 'Runsheets', perm: 'parcels.assign' },
      { to: '/drivers/map',    icon: MapPin,   label: 'Driver Map', perm: 'drivers.track' },
      { to: '/barcodes',       icon: Tag,      label: 'Barcodes', perm: 'labels.manage' },
      { to: '/label-tracking', icon: ScanLine, label: 'Label Tracking', perm: 'labels.manage' },
    ],
  },
  {
    group: 'system', label: 'System', icon: Settings, items: [
      { to: '/activity-log', icon: ClipboardList, label: 'Activity Log', perm: 'system.audit' },
      { to: '/email-log',    icon: Mail,          label: 'Email Log', perm: 'system.audit' },
      { to: '/settings',     icon: Settings,      label: 'Settings' },
    ],
  },
];

const linkClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
    isActive ? 'bg-orange-500 text-white' : 'text-gray-300 hover:bg-gray-800'
  }`;

function NavGroup({ group, onNavigate }) {
  const location = useLocation();
  const visible = group.items.filter((i) => !i.perm || can(i.perm));
  const hasActive = visible.some((i) => location.pathname.startsWith(i.to));
  // Remember whether each group was left open, but never hide the page you're on
  const [open, setOpen] = useState(() => {
    const saved = localStorage.getItem(`nav_${group.group}`);
    return saved === null ? true : saved === '1';
  });
  const show = open || hasActive;

  const toggle = () => {
    const next = !show;
    setOpen(next);
    localStorage.setItem(`nav_${group.group}`, next ? '1' : '0');
  };

  if (visible.length === 0) return null;
  const Icon = group.icon;

  return (
    <div>
      <button onClick={toggle}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-400 hover:bg-gray-800 hover:text-gray-200 transition-colors">
        <Icon size={18} />
        <span className="flex-1 text-left">{group.label}</span>
        {show ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {show && (
        <div className="mt-0.5 ml-3 pl-3 border-l border-gray-700 space-y-0.5">
          {visible.map(({ to, icon: ItemIcon, label }) => (
            <NavLink key={to} to={to} end onClick={onNavigate.close} className={linkClass}>
              <ItemIcon size={15} />
              {label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

// Shown instead of the app when the office issued this password. It gets sent
// over WhatsApp or email and stays in the thread, so it isn't private until
// the user picks their own. No way past it except changing or signing out.
function MustChangePassword({ name, onDone, onLogout }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error('Your new password must be at least 6 characters');
    if (next !== confirm) return toast.error('The two new passwords are different');
    setBusy(true);
    try {
      await api.post('/auth/change-password', { currentPassword: current, newPassword: next });
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...u, must_change_password: false }));
      toast.success('Password changed — only you know it now');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not change it');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-8">
        <div className="text-center mb-5">
          <p className="text-4xl mb-2">🔒</p>
          <h1 className="text-xl font-bold text-gray-800">Choose your password</h1>
          <p className="text-xs text-gray-400 mt-1">Hi {(name || '').split(' ')[0] || 'there'} — one quick thing first</p>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          The password the office sent you is sitting in your messages, where other people could
          see it. Pick your own now so only you know it.
        </p>
        {[['Password the office sent', current, setCurrent, 'The one from the message'],
          ['Your new password', next, setNext, 'At least 6 characters'],
          ['Type it again', confirm, setConfirm, 'The same new password']].map(([label, val, set, ph]) => (
          <div key={label} className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
            <input type="password" value={val} onChange={(e) => set(e.target.value)} placeholder={ph}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
        ))}
        <button type="submit" disabled={busy}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2.5 rounded-lg font-semibold disabled:opacity-50 mt-2">
          {busy ? 'Saving…' : 'Save my password'}
        </button>
        <button type="button" onClick={onLogout} className="w-full text-xs text-gray-400 hover:text-gray-600 mt-4">
          Sign out instead
        </button>
      </form>
    </div>
  );
}

// "Install app" appears only when the browser offers to install the dashboard
// (Chrome/Edge on desktop and Android). Once installed it never shows again.
function InstallHint() {
  const [prompt, setPrompt] = useState(null);
  React.useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', () => setPrompt(null));
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);
  if (!prompt) return null;
  const install = async () => {
    prompt.prompt();
    try { await prompt.userChoice; } catch { /* dismissed */ }
    setPrompt(null);
  };
  return (
    <button onClick={install} title="Add Expressway to your desktop or home screen — opens like an app, no browser tabs"
      className="w-full mb-3 flex items-center justify-center gap-2 bg-orange-500/20 hover:bg-orange-500/30 text-orange-300 text-xs font-medium px-3 py-2 rounded-lg">
      <Smartphone size={14} /> Install app
    </button>
  );
}

export default function Layout() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [, forceRender] = useState(0);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Fresh role + permissions on every load: a role edit reaches the menu
  // without signing out. Failures are ignored — the stored copy still works.
  React.useEffect(() => {
    refreshUser().then(() => forceRender((n) => n + 1)).catch(() => {});
  }, []);

  const logout = () => {
    localStorage.clear();
    navigate('/login');
  };

  if (user.must_change_password) {
    return <MustChangePassword name={user.name} onLogout={logout} onDone={() => forceRender((n) => n + 1)} />;
  }

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Anything loading anywhere in the app, including the silent refreshes */}
      <GlobalLoadingBar />

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-30 w-56 bg-gray-900 text-white flex flex-col transform transition-transform ${open ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:static md:flex`}>
        <div className="p-4 border-b border-gray-700">
          <p className="font-bold text-orange-400 text-lg leading-tight">Expressway</p>
          <p className="text-xs text-gray-400">Courier Management</p>
        </div>
        <nav className="flex-1 py-4 space-y-1 px-2 overflow-y-auto">
          {nav.filter((entry) => entry.group || !entry.perm || can(entry.perm)).map((entry) =>
            entry.group ? (
              <NavGroup key={entry.group} group={entry}
                onNavigate={{ close: () => setOpen(false) }} />
            ) : (
              <NavLink key={entry.to} to={entry.to} end={entry.to === '/dashboard'}
                onClick={() => setOpen(false)} className={linkClass}>
                <entry.icon size={18} />
                {entry.label}
              </NavLink>
            )
          )}
        </nav>
        <div className="p-4 border-t border-gray-700">
          <InstallHint />
          <p className="text-xs text-gray-400 mb-1">{user.name}</p>
          <button onClick={logout} className="flex items-center gap-2 text-sm text-gray-400 hover:text-white">
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {open && <div className="fixed inset-0 z-20 bg-black/50 md:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="bg-white shadow-sm px-4 py-3 flex items-center gap-3 md:hidden">
          <button onClick={() => setOpen(true)}><Menu size={22} /></button>
          <span className="font-semibold text-orange-500">Expressway Courier</span>
        </header>
        <SosWatcher isAdmin={can('drivers.track')} />
        <BackupWarning />
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
